import { AppShell, PageTitle } from "@/components/app-shell";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { TableSkeleton } from "@/components/mission-skeletons";
import { PresenceRail } from "@/components/presence-rail";
import {
  COLLAB_IA,
  canWriteCollaboration,
  issueStatusLabel,
  openIssueCount,
  requireIssueGitRef,
  writeIssueEvent,
} from "@/lib/collaboration";
import { addIssueComment } from "@/lib/issue-actions";
import { consumePendingMissionDraft, MISSION_DRAFT_ISSUE_EVENT } from "@/lib/mission-drive";
import { useProjectScope } from "@/lib/project-scope";
import { issuesHref } from "@/lib/underline-nav";
import { cn } from "@/lib/utils";

import {
  artifactUrl,
  createIssue,
  filesForRevision,
  filterTree,
  getHardware,
  getIssue,
  type IssueComment,
  type HardwareRevision,
  type Issue,
  type IssueAttachment,
  isKicadSch,
  listIssues,
  shortOid,
  uploadIssueScreenshot,
} from "@/lib/api";
import { useCallback, useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { scopeLoadKey, useKeyedLoad } from "@/lib/scope-loading";

export function IssuesPage() {
  const { issueId } = useParams();
  if (issueId) return <IssueDetail key={issueId} />;
  return <IssueList />;
}

function IssueList() {
  const { orgSlug, projectSlug } = useParams();
  const { role } = useProjectScope();
  const canWrite = canWriteCollaboration(role);
  const [items, setItems] = useState<Issue[]>([]);
  const [revisions, setRevisions] = useState<HardwareRevision[]>([]);
  const [filesByOid, setFilesByOid] = useState<Record<string, string[]>>({});
  const [createOpen, setCreateOpen] = useState(false);
  const [draftSeed, setDraftSeed] = useState<{ title: string; body: string } | null>(null);
  const [params] = useSearchParams();
  const statusFilter = params.get("filter") || "all";
  const [err, setErr] = useState<string | null>(null);
  const routeKey = scopeLoadKey(orgSlug, projectSlug);
  const { loading, start, finish } = useKeyedLoad(routeKey);
  const nav = useNavigate();
  const base = orgSlug && projectSlug ? `/o/${orgSlug}/p/${projectSlug}` : "";

  const load = useCallback(async () => {
    const isCurrent = start();
    if (!orgSlug || !projectSlug) {
      finish(isCurrent);
      return;
    }
    try {
      const [iss, hw] = await Promise.all([listIssues(orgSlug, projectSlug), getHardware(orgSlug, projectSlug)]);
      if (!isCurrent()) return;
      setItems(iss.items ?? []);
      setRevisions(hw.revisions ?? []);
      const map: Record<string, string[]> = {};
      for (const r of hw.revisions ?? []) {
        map[r.git_oid] = filterTree(filesForRevision(hw.artifacts ?? [], r), true).filter(isKicadSch);
      }
      setFilesByOid(map);
      setErr(null);
    } catch (e) {
      if (!isCurrent()) return;
      setItems([]);
      setErr(e instanceof Error ? e.message : "issues load failed");
    } finally {
      finish(isCurrent);
    }
  }, [orgSlug, projectSlug, start, finish]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const apply = (title: string, body: string) => {
      if (!canWrite) return;
      setDraftSeed({ title, body });
      setCreateOpen(true);
    };
    const pending = consumePendingMissionDraft();
    if (pending) apply(pending.title, pending.body);
    const onDraft = (event: Event) => {
      const detail = (event as CustomEvent<{ title?: string; body?: string }>).detail;
      apply(detail?.title ?? "", detail?.body ?? "");
    };
    window.addEventListener(MISSION_DRAFT_ISSUE_EVENT, onDraft);
    return () => window.removeEventListener(MISSION_DRAFT_ISSUE_EVENT, onDraft);
  }, [canWrite]);

  const visible = items.filter((i) => statusFilter === "all" || i.status === statusFilter);

  return (
    <AppShell
      title={
        <PageTitle
          kicker={COLLAB_IA.issuesKicker}
          extra={
            canWrite ? (
              <Button
                size="sm"
                className="bg-falcon text-white hover:bg-falcon/90"
                onClick={() => setCreateOpen(true)}
                data-testid="new-issue"
                data-mission-id="action:new-issue"
                data-mission-panel="issues"
              >
                New Issue
              </Button>
            ) : null
          }
        >
          Issues
          <span className="font-mono text-[12px] font-normal text-muted-foreground" data-testid="issues-open-count">
            {openIssueCount(items)} open
          </span>
        </PageTitle>
      }
    >
      <div className="mb-3" data-testid="issues-ia">
        <PresenceRail orgSlug={orgSlug} projectSlug={projectSlug} surface="issues" />
        <p className="mt-2 text-[11px] text-muted-foreground">
          Issues are trackers with a required git_ref — not Changes, and they never approve a Review.{" "}
          {COLLAB_IA.noMerge} Guest and viewer are read-only.
        </p>
      </div>
      {err && <p className="mb-3 text-sm text-muted-foreground">{err}</p>}
      {loading ? (
        <TableSkeleton columns={5} testId="issues-skeleton" />
      ) : visible.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="issues-empty">
          No issues yet. A tracker needs a git_ref (revision oid or branch) — create will not invent one.
        </p>
      ) : (
        <div
          className="overflow-hidden rounded-[4px] border border-border bg-card"
          data-testid="issues-list"
        >
          {visible.map((i) => (
            <Link
              key={i.id}
              to={issuesHref(base, { id: i.id, git_ref: i.git_ref })}
              data-testid={`issue-row-${i.id}`}
              data-mission-id={`issue:${i.id}`}
              className={cn(
                "flex items-center gap-3 border-b border-border border-l-2 border-l-transparent px-4 py-2.5 no-underline last:border-b-0",
                "hover:border-l-[var(--copper)] hover:bg-secondary",
              )}
            >
              <span className="w-12 shrink-0 font-mono text-[12px] text-muted-foreground">I-{i.number}</span>
              <span className="min-w-0 flex-1 truncate text-[13px] text-foreground">{i.title}</span>
              <span className="hidden font-mono text-[11px] text-muted-foreground sm:inline" data-testid="issue-row-git-ref">
                {i.git_ref || "git_ref missing"}
              </span>
              {i.path ? (
                <span className="hidden max-w-[180px] truncate font-mono text-[11px] text-muted-foreground md:inline">
                  {i.path}
                </span>
              ) : null}
              <IssueStatusPill status={i.status} />
            </Link>
          ))}
        </div>
      )}
      <NewIssueDialog
        open={createOpen}
        onOpenChange={(next) => {
          setCreateOpen(next);
          if (!next) setDraftSeed(null);
        }}
        orgSlug={orgSlug}
        projectSlug={projectSlug}
        revisions={revisions}
        filesByOid={filesByOid}
        draft={draftSeed}
        onCreated={(id, gitRef) => {
          void load();
          if (base) nav(issuesHref(base, { id, git_ref: gitRef }));
        }}
      />
    </AppShell>
  );
}

function IssueDetail() {
  const { orgSlug, projectSlug, issueId } = useParams();
  const { role } = useProjectScope();
  const canWrite = canWriteCollaboration(role);
  const [params, setParams] = useSearchParams();
  const [current, setCurrent] = useState<Issue | null>(null);
  const [attachments, setAttachments] = useState<IssueAttachment[]>([]);
  const [comments, setComments] = useState<IssueComment[]>([]);
  const [comment, setComment] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const routeKey = `${scopeLoadKey(orgSlug, projectSlug)}/${issueId ?? ""}`;
  const { loading, start, finish } = useKeyedLoad(routeKey);
  const base = orgSlug && projectSlug ? `/o/${orgSlug}/p/${projectSlug}` : "";
  const urlRef = params.get("git_ref");

  const load = useCallback(async () => {
    const isCurrent = start();
    if (!orgSlug || !projectSlug || !issueId) {
      finish(isCurrent);
      return;
    }
    try {
      const body = await getIssue(orgSlug, projectSlug, issueId);
      if (!isCurrent()) return;
      setCurrent(body.issue);
      setAttachments(body.attachments ?? []);
      setComments(body.comments ?? []);
      setErr(null);
    } catch (e) {
      if (!isCurrent()) return;
      setCurrent(null);
      setAttachments([]);
      setComments([]);
      setErr(e instanceof Error ? e.message : "issue load failed");
    } finally {
      finish(isCurrent);
    }
  }, [orgSlug, projectSlug, issueId, start, finish]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const ref = requireIssueGitRef(current?.git_ref);
    if (!ref) return;
    if (urlRef !== ref) {
      setParams({ git_ref: ref }, { replace: true });
    }
  }, [current?.git_ref, urlRef, setParams]);

  async function onComment() {
    if (!orgSlug || !projectSlug || !issueId || !comment.trim()) return;
    setBusy(true);
    setErr(null);
    try {
      const saved = await addIssueComment(orgSlug, projectSlug, issueId, comment);
      setComments((prev) => [...prev, saved]);
      setComment("");
    } catch (e) {
      setErr(e instanceof Error ? e.message : "comment failed");
    } finally {
      setBusy(false);
    }
  }

  async function onToggleStatus() {
    if (!orgSlug || !projectSlug || !issueId || !current) return;
    setBusy(true);
    try {
      await writeIssueEvent(orgSlug, projectSlug, issueId, {
        type: "status",
        status: current.status === "open" ? "closed" : "open",
      });
      await load();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "status failed");
    } finally {
      setBusy(false);
    }
  }

  const gitRef = requireIssueGitRef(current?.git_ref) ?? requireIssueGitRef(urlRef);

  return (
    <AppShell
      title={
        <PageTitle
          kicker={COLLAB_IA.issuesKicker}
          extra={
            <Button size="sm" variant="outline" asChild>
              <Link to={issuesHref(base)}>All issues</Link>
            </Button>
          }
        >
          {current ? `I-${current.number} ${current.title}` : "Issue"}
          {gitRef ? (
            <span
              className="font-mono text-[12px] font-normal text-muted-foreground"
              data-testid="issue-git-ref"
            >
              {gitRef}
            </span>
          ) : null}
        </PageTitle>
      }
    >
      <div className="mb-3" data-testid="issues-ia">
        <PresenceRail orgSlug={orgSlug} projectSlug={projectSlug} surface="issues" objectId={issueId} />
        <p className="mt-2 text-[11px] text-muted-foreground">
          This is an Issue tracker, not a Change. Close is manual — Issues never approve Reviews. {COLLAB_IA.noMerge}
        </p>
      </div>
      {err ? (
        <p
          className="mb-3 rounded-[4px] border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive"
          role="alert"
        >
          {err}
        </p>
      ) : null}
      {loading ? (
        <TableSkeleton columns={3} testId="issues-skeleton" />
      ) : !current ? (
        <p className="text-sm text-muted-foreground">Issue not found.</p>
      ) : (
        <div className="space-y-4" data-testid="issue-detail" data-mission-id={issueId ? `issue:${issueId}` : undefined}>
          <div className="overflow-hidden rounded-[4px] border border-border bg-card">
            <div className="flex flex-wrap items-center gap-2 border-b border-border px-4 py-2.5">
              <IssueStatusPill status={current.status} />
              <span className="font-mono text-[11px] text-muted-foreground">I-{current.number}</span>
              {canWrite ? (
                <Button size="xs" variant="outline" disabled={busy} onClick={() => void onToggleStatus()}>
                  {current.status === "open" ? "Close" : "Reopen"}
                </Button>
              ) : null}
            </div>
            <div className="space-y-3 px-4 py-3">
              <div className="space-y-1">
                <Label>git_ref</Label>
                {gitRef ? (
                  <div
                    className="rounded-[4px] border border-border bg-secondary px-3 py-2 font-mono text-xs"
                    data-testid="issue-detail-git-ref"
                  >
                    {gitRef}
                  </div>
                ) : (
                  <p className="text-sm text-destructive" data-testid="issue-git-ref-missing">
                    git_ref is required. This row cannot be treated as a tracker.
                  </p>
                )}
              </div>
              <div className="space-y-1">
                <Label>path</Label>
                <div className="rounded-[4px] border border-border bg-secondary px-3 py-2 font-mono text-xs">
                  {current.path || "—"}
                </div>
              </div>
              {current.body ? <p className="text-sm whitespace-pre-wrap">{current.body}</p> : null}
              {comments.length > 0 ? (
                <div className="space-y-2" data-testid="issue-comments">
                  <Label>comments</Label>
                  <ul className="space-y-2">
                    {comments.map((c) => (
                      <li
                        key={c.id}
                        className="rounded-[4px] border border-border bg-secondary px-3 py-2 text-sm"
                        data-testid="issue-comment"
                        data-comment-id={c.id}
                      >
                        <div className="mb-1 flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground">
                          <span data-testid="issue-comment-author">
                            {c.origin === "think" || c.author_origin_marker === "think"
                              ? "由 Think 代写"
                              : c.author_display_name ?? "成员"}
                          </span>
                          <time dateTime={c.created_at}>{new Date(c.created_at).toLocaleString()}</time>
                        </div>
                        <p className="whitespace-pre-wrap">{c.body}</p>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
              {attachments.length > 0 && orgSlug && projectSlug && (
                <div className="space-y-2">
                  <Label>screenshots</Label>
                  {attachments.map((a) => (
                    <img
                      key={a.id}
                      src={artifactUrl(orgSlug, projectSlug, a.id)}
                      alt={a.path || a.id}
                      className="max-h-40 rounded-[4px] border border-border"
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
          {canWrite ? (
            <div className="space-y-2 rounded-[4px] border border-border bg-card p-4">
              <Label htmlFor="iss-comment">comment</Label>
              <Textarea
                id="iss-comment"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Note on this git_ref — not a Review decide."
              />
              <Button size="sm" variant="outline" disabled={busy || !comment.trim()} onClick={() => void onComment()}>
                {busy ? "Posting…" : "Comment"}
              </Button>
            </div>
          ) : (
            <p className="text-[11px] text-muted-foreground">Guest and viewer are read-only.</p>
          )}
        </div>
      )}
    </AppShell>
  );
}

function IssueStatusPill({ status }: { status: string }) {
  const open = status === "open";
  return (
    <span
      data-testid="issue-status"
      className={cn(
        "rounded-full border px-2 py-0.5 text-[11px] tracking-[0.03em]",
        open ? "border-emerald-600/70 text-emerald-400" : "border-border text-muted-foreground",
      )}
    >
      {issueStatusLabel(status)}
    </span>
  );
}

function NewIssueDialog({
  open,
  onOpenChange,
  orgSlug,
  projectSlug,
  revisions,
  filesByOid,
  draft,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  orgSlug?: string;
  projectSlug?: string;
  revisions: HardwareRevision[];
  filesByOid: Record<string, string[]>;
  draft?: { title: string; body: string } | null;
  onCreated: (id: string, gitRef: string) => void;
}) {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [gitRef, setGitRef] = useState("");
  const [path, setPath] = useState("");
  const [refdes, setRefdes] = useState("");
  const [pinNumber, setPinNumber] = useState("");
  const [pinName, setPinName] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const sheets = useMemo(() => {
    const fromRev = filesByOid[gitRef] ?? [];
    if (fromRev.length) return fromRev;
    return Object.values(filesByOid).flat();
  }, [filesByOid, gitRef]);

  useEffect(() => {
    if (!open) return;
    if (!gitRef && revisions[0]) setGitRef(revisions[0].git_oid);
  }, [open, gitRef, revisions]);

  useEffect(() => {
    if (!open || !draft) return;
    setTitle(draft.title);
    setBody(draft.body);
  }, [open, draft]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!orgSlug || !projectSlug) return;
    const ref = requireIssueGitRef(gitRef);
    if (!title.trim() || !ref) {
      setErr("title and git_ref required — create will not invent a ref");
      return;
    }
    setBusy(true);
    setErr(null);
    const sheet = path.trim();
    const anchor_json: Record<string, unknown> = pinNumber.trim()
      ? { v1_kind: "pin", sheet_path: sheet, refdes: refdes.trim(), unit: 1, pin_number: pinNumber.trim(), pin_name: pinName.trim() || undefined }
      : refdes.trim()
        ? { v1_kind: "component", sheet_path: sheet, refdes: refdes.trim() }
        : files.length > 0
          ? { v1_kind: "image" }
          : { v1_kind: "general" };
    try {
      const created = await createIssue(orgSlug, projectSlug, {
        title: title.trim(),
        git_ref: ref,
        path: sheet || undefined,
        body: body.trim() || undefined,
        workstream: "hardware",
        anchor_kind: "general",
        anchor_json,
      });
      for (const file of files) {
        await uploadIssueScreenshot(orgSlug, projectSlug, created.id, file);
      }
      setTitle("");
      setBody("");
      setPath("");
      setRefdes("");
      setPinNumber("");
      setPinName("");
      setFiles([]);
      onOpenChange(false);
      onCreated(created.id, ref);
    } catch (ex) {
      setErr(ex instanceof Error ? ex.message : "create failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>New Issue</DialogTitle>
          <DialogDescription>
            git_ref is required. Create refuses a silent tracker without a ref. Screenshots are optional. Close stays
            manual — Issues do not approve Reviews.
          </DialogDescription>
        </DialogHeader>
        <form className="space-y-3" onSubmit={onSubmit}>
          <div className="space-y-1.5">
            <Label htmlFor="iss-title">title</Label>
            <Input id="iss-title" required value={title} onChange={(e) => setTitle(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="iss-git-ref">git_ref</Label>
            <Input
              id="iss-git-ref"
              required
              className="font-mono"
              value={gitRef}
              onChange={(e) => setGitRef(e.target.value)}
              placeholder="revision oid or branch"
              data-testid="create-issue-git-ref"
            />
            {revisions.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {revisions.map((r) => (
                  <Button
                    key={r.id}
                    type="button"
                    size="xs"
                    variant={gitRef === r.git_oid ? "default" : "outline"}
                    onClick={() => setGitRef(r.git_oid)}
                  >
                    {r.label || shortOid(r.git_oid)}
                  </Button>
                ))}
              </div>
            )}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="iss-path">path</Label>
            <Input
              id="iss-path"
              className="font-mono"
              list="iss-path-list"
              value={path}
              onChange={(e) => setPath(e.target.value)}
              placeholder="Ethernet_sch.kicad_sch"
            />
            <datalist id="iss-path-list">
              {sheets.map((s) => (
                <option key={s} value={s} />
              ))}
            </datalist>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="iss-body">body</Label>
            <Textarea id="iss-body" value={body} onChange={(e) => setBody(e.target.value)} />
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div className="space-y-1.5">
              <Label htmlFor="iss-refdes">refdes</Label>
              <Input id="iss-refdes" className="font-mono" value={refdes} onChange={(e) => setRefdes(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="iss-pin">pin</Label>
              <Input id="iss-pin" className="font-mono" value={pinNumber} onChange={(e) => setPinNumber(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="iss-pin-name">pin_name</Label>
              <Input id="iss-pin-name" className="font-mono" value={pinName} onChange={(e) => setPinName(e.target.value)} />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="iss-shots">screenshots (optional)</Label>
            <Input
              id="iss-shots"
              type="file"
              accept="image/*"
              multiple
              onChange={(e) => setFiles(Array.from(e.target.files ?? []))}
            />
            <p className="text-xs text-muted-foreground">Recommended for pin/net disputes. Not required to create.</p>
          </div>
          {err && (
            <p className="text-sm text-destructive" data-testid="create-issue-error">
              {err}
            </p>
          )}
          <DialogFooter>
            <Button type="submit" disabled={busy} className="bg-falcon text-white hover:bg-falcon/90" data-mission-danger="">
              {busy ? "Creating…" : "Create"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
